import React, {
  ClipboardEvent,
  FocusEventHandler,
  FunctionComponent as FC,
  useEffect,
  useRef,
} from 'react';
import { ICommentBoxProps, ICommentBoxValue } from './ICommentBoxProps';
import './CommentBox.scss';

export const CommentBox: FC<ICommentBoxProps> = (props) => {
  const TYPE_TEXT = 'text';
  const TYPE_BREAK = 'break';
  const TYPE_IMAGE = 'image';
  const IMAGE_PREFIX = 'comment-image';
  const divRef = useRef<HTMLDivElement>(null);
  const pasteNode = (node: Node) => {
    const selection = window.getSelection();
    if (selection instanceof Selection) {
      const range: Range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(node);
      range.setStartAfter(node);
      range.setEndAfter(node);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const checkEmpty = () => {
    const textContent = divRef?.current?.textContent || '';
    const files = divRef?.current?.getElementsByTagName('img') || [];

    if (props?.excludeTextRegex && props?.excludeTextRegex.test(textContent)) {
      props?.onRegexError?.(true);
    } else {
      props?.onRegexError?.(false);
    }

    const innerHtml = textContent?.trim() || '';
    const isEmpty = innerHtml.length === 0 && files.length === 0;
    props?.isEmpty?.(isEmpty);
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    // Get the clipboard data
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const items: DataTransferItemList = clipboardData?.items || [];

    if (items.length > 0) {
      const item = items[0];

      if (item.type.indexOf('image') !== -1) {
        const reader = new FileReader();
        const containerWidth = divRef?.current?.clientWidth || 100;
        const imgWidth = (containerWidth * 90) / 100;
        const imgName = item.getAsFile()?.name;

        reader.onload = function (event: ProgressEvent<FileReader>) {
          const img: HTMLImageElement = new Image();
          img.onload = function () {
            const div = document.createElement('div');
            div.innerHTML = `<img width="${imgWidth}" alt="${imgName}" src="${img.src}"/>`;
            const node = div.firstChild as Node;
            pasteNode(node);
            checkEmpty();
          };
          img.src = (event?.target?.result as string) || '';
        };

        reader.readAsDataURL(item.getAsFile() as Blob);
      } else if (item.type.indexOf('text') !== -1) {
        const text = clipboardData?.getData('text/plain');
        const textNode = document.createTextNode(text);
        pasteNode(textNode);
        checkEmpty();
      }
    }
  };

  const reduceFn = (
    previousValue: ICommentBoxValue[],
    currentValue: ICommentBoxValue
  ) => {
    const finalValue = previousValue.at(-1);

    if (currentValue&&currentValue.type === TYPE_BREAK) return previousValue;

    if (
      finalValue &&
      finalValue.type === TYPE_TEXT &&
      currentValue&&currentValue.type === TYPE_TEXT
    ) {
      previousValue.pop();
      const finalContent = finalValue?.content ?? '';
      const currContent = currentValue?.content ?? '';
      const updatedFinalValue = {
        ...finalValue,
        content: finalContent + ' ' + currContent,
      };
      previousValue.push(updatedFinalValue);
    } else {
      previousValue.push(currentValue);
    }
    return previousValue;
  };

  const getValue: FocusEventHandler<HTMLDivElement> = async (e) => {
    const divElement = divRef?.current;

    if (divElement instanceof HTMLDivElement) {
      const contentArray = await getValueElements(divElement);
      const flatArray = contentArray.flat();
      const reducedArray = flatArray.reduce(reduceFn, []);
      props?.onChange?.(reducedArray);
    }
  };

  const base64ToFile = (base64Image: string, imageName: string) => {
    const byteCharacters = window.atob(base64Image.split(',')[1]);
    const byteArrays = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteArrays)], { type: 'image/png' });
    const file = new File([blob], imageName, { type: 'image/png' });
    return file;
  };

  const getValueElements = (
    divElement: HTMLDivElement,
    contentArray: any[] = []
  ) =>
    new Promise<any[]>(async (resolve) => {
      const childNodesLength = divElement.childNodes.length;
      divElement.childNodes.forEach(async (childNode, i) => {
        const node = childNode as HTMLDivElement | HTMLImageElement;

        const elementNode = node.nodeType === Node.ELEMENT_NODE;

        if (node.nodeType === Node.TEXT_NODE) {
          contentArray.push({ type: TYPE_TEXT, content: node.textContent });
        } else if (elementNode && node.tagName.toLowerCase() === 'br') {
          contentArray.push({ type: TYPE_BREAK, content: '\n' });
        } else if (elementNode && node.tagName.toLowerCase() === 'div') {
          contentArray.push(await getValueElements(node));
        } else if (elementNode && node.tagName.toLowerCase() === 'img') {
          const base64Image = (node as HTMLImageElement).src;
          const imageName =
            (node as HTMLImageElement).alt || `${IMAGE_PREFIX}-${i}.png`;
          const file = base64ToFile(base64Image, imageName);
          contentArray.push({
            type: TYPE_IMAGE,
            content: (node as HTMLImageElement).src,
            name: imageName,
            file,
          });
        }
        if (i === childNodesLength - 1) {
          const reducedContent = contentArray.reduce(reduceFn, []);
          return resolve(reducedContent);
        }
      });
    });

  useEffect(() => {
    if (props?.makeCommentEmpty && divRef?.current?.innerHTML) {
      divRef.current.innerHTML = '';
      props?.setMakeCommentEmpty?.(false);
      checkEmpty();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.makeCommentEmpty, checkEmpty]);
  return (
    <div
      contentEditable
      className={`comment-box ${props?.className || ''}`}
      data-testid='comment-box'
      ref={divRef}
      onPaste={handlePaste}
      onInput={checkEmpty}
      onBlur={getValue}
      data-placeholder={props?.placeholder}
    />
  );
};
export default CommentBox;
